<!-- Console action feedback 0.1.155; 2026-10-28 18:11:13 CST; parent 44dbda7a05c960e3c79c6c322d676ac5e4982b20. -->
<!-- HotX Console 0.1.121 空态返回域名管理默认首页。实际修改时间：2026-09-29 13:25:25 CST；精确父基线：2b265b8475709018e4bf7099ec6a081871eb4c6b；变更：CONSOLE-HOME-DATA-PASSWORD-20260929。 -->
<!-- Console 0.1.137; 2026-10-07 01:19:11 CST; parent 941991113bff4331e0f76f14ba9e8fd7a2821d2c. Cache settings save errors and pending-publish feedback. -->
<script setup lang="ts">
import { ElMessage } from 'element-plus';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import EmptyState from '@hotx-cdn/shared/components/EmptyState.vue';
import { CONSOLE_SESSION_CHANGED_EVENT, consoleApi, hasConsoleSession } from '../api/client';
import { setConsoleNavigationDirty } from '../router/navigationState';
import { publishCurrentDomainConfig } from '../utils/autoPublish';
import type { CacheOperation, CacheProfile, Domain } from '../api/types';
import { displayDateTime } from '../utils/formatters';

/*
 * Cache boundary:
 * Cache rule saves use current-domain auto-publish.
 * PURGE/PRELOAD operations are real edge tasks; frontend "SUCCEEDED" must correspond to backend per-node tasks and Agent execution.
 * purgeAll remains outside the current safe path unless separately designed and production-verified.
 */
const domains = ref<Domain[]>([]);
const selectedDomainId = ref<number>();
const profile = ref<CacheProfile | null>(null);
const error = ref('');
const success = ref('');
const profileBaseline = ref('');
const savingProfile = ref(false);
const refreshType = ref<'URL' | 'DIRECTORY'>('URL');
const refreshContent = ref('');
const refreshRecords = ref<CacheOperation[]>([]);
const submittingRefresh = ref(false);
const latestRefreshTask = ref<CacheOperation | null>(null);

async function loadDomains() {
  if (!hasConsoleSession()) {
    domains.value = [];
    selectedDomainId.value = undefined;
    profile.value = null;
    refreshRecords.value = [];
    latestRefreshTask.value = null;
    setConsoleNavigationDirty(false);
    return;
  }
  domains.value = await consoleApi.domains.list();
  selectedDomainId.value = domains.value[0]?.id;
}

function handleConsoleSessionChanged() {
  void loadDomains();
}

onMounted(() => {
  window.addEventListener(CONSOLE_SESSION_CHANGED_EVENT, handleConsoleSessionChanged);
  void loadDomains();
});
onBeforeUnmount(() => {
  window.removeEventListener(CONSOLE_SESSION_CHANGED_EVENT, handleConsoleSessionChanged);
  setConsoleNavigationDirty(false);
});

watch(selectedDomainId, loadProfile);
watch(profile, value => {
  setConsoleNavigationDirty(Boolean(value && profileBaseline.value && JSON.stringify(value) !== profileBaseline.value));
}, { deep: true });

async function loadProfile() {
  if (!selectedDomainId.value) return;
  try {
    profile.value = await consoleApi.cache.profile(selectedDomainId.value);
    await loadOperations();
    profileBaseline.value = JSON.stringify(profile.value);
    setConsoleNavigationDirty(false);
  } catch (e) {
    error.value = e instanceof Error ? e.message : '缓存规则加载失败';
  }
}

async function loadOperations() {
  if (!selectedDomainId.value) return;
  refreshRecords.value = await consoleApi.cache.operations(selectedDomainId.value);
}

async function saveProfile() {
  if (!selectedDomainId.value || !profile.value || savingProfile.value) return;
  savingProfile.value = true;
  error.value = '';
  success.value = '';
  try {
    await consoleApi.cache.updateProfile(selectedDomainId.value, profile.value);
    profileBaseline.value = JSON.stringify(profile.value);
    setConsoleNavigationDirty(false);
    const publish = await publishCurrentDomainConfig(
      selectedDomainId.value,
      `客户保存 ${domainName(selectedDomainId.value)} 缓存配置后生成当前域名配置包`
    );
    if (publish && !publish.ok) { error.value = publish.message; ElMessage.warning(error.value); }
    else success.value = publish
      ? `缓存规则已保存，配置包${publish.packageId ? ` ${publish.packageId}` : ''}已生成；节点生效情况请查看发布状态。`
      : '缓存规则已保存';
    if (success.value) ElMessage.success(success.value);
  } catch (e) {
    error.value = e instanceof Error ? e.message : '缓存规则保存失败，请重试';
    ElMessage.error(error.value);
  } finally {
    savingProfile.value = false;
  }
}

async function submitRefresh() {
  if (submittingRefresh.value) return;
  if (!selectedDomainId.value) {
    error.value = '请先选择域名';
    return;
  }
  if (!refreshContent.value.trim()) {
    error.value = '请输入需要刷新的 URL 或目录';
    return;
  }
  error.value = '';
  success.value = '';
  const lines = refreshContent.value.split('\n').map(item => item.trim()).filter(Boolean);
  if (!lines.length) {
    error.value = '请输入有效的 URL 或目录';
    return;
  }
  const body = {
    purgeAll: false,
    targets: lines
  };
  try {
    submittingRefresh.value = true;
    const task = await consoleApi.cache.purge(selectedDomainId.value, body) as CacheOperation;
    latestRefreshTask.value = task;
    refreshContent.value = '';
    success.value = `${refreshTypeText()}任务已提交：${lines.length} 个对象，任务 ID ${task.id || '-'}，当前状态 ${operationStatusText(task.status || 'QUEUED')}。`;
    ElMessage.success(success.value);
    try { await loadOperations(); }
    catch { ElMessage.warning('任务已提交，操作记录暂未刷新，请稍后重新查看。'); }
  } catch (e) {
    error.value = e instanceof Error ? e.message : '刷新任务提交失败';
    ElMessage.error(error.value);
  } finally {
    submittingRefresh.value = false;
  }
}

function domainName(domainId: number) {
  return domains.value.find(domain => domain.id === domainId)?.domain || '当前域名';
}

function operationTypeText(operationType: string) {
  if (operationType === 'PURGE_DIRECTORY') return '目录刷新';
  if (operationType === 'PRELOAD') return '缓存预热';
  return 'URL 刷新';
}

function refreshTypeText() {
  return refreshType.value === 'DIRECTORY' ? '目录刷新' : 'URL 刷新';
}

function operationStatusText(status: string) {
  const names: Record<string, string> = {
    PENDING: '排队中',
    QUEUED: '排队中',
    RUNNING: '处理中',
    SUCCEEDED: '已完成',
    FAILED: '失败',
    CANCELLED: '已取消'
  };
  return names[status] || status;
}

function operationResult(operation: CacheOperation) {
  return operation.lastError || (operation.status === 'SUCCEEDED' ? '成功' : '等待执行');
}

function operationTimeText(operation: CacheOperation) {
  return displayDateTime(operation.createdAt);
}
</script>

<template>
  <section class="page">
    <div class="page-title">
      <h1>刷新缓存</h1>
      <p>提交刷新任务，清理缓存内容并让网站最新内容尽快生效。</p>
    </div>
    <el-alert v-if="error" :title="error" type="error" show-icon />
    <el-alert v-if="success" :title="success" type="success" show-icon />
    <el-select v-model="selectedDomainId" :disabled="savingProfile" placeholder="选择域名" class="domain-select">
      <el-option v-for="domain in domains" :key="domain.id" :label="domain.domain" :value="domain.id" />
    </el-select>

    <div class="two-column">
      <el-card>
        <template #header>刷新缓存</template>
        <el-form label-position="top">
          <el-form-item label="刷新类型">
            <el-radio-group v-model="refreshType">
              <el-radio-button label="URL">URL 刷新</el-radio-button>
              <el-radio-button label="DIRECTORY">目录刷新</el-radio-button>
            </el-radio-group>
          </el-form-item>
          <el-form-item label="输入 URL 或目录">
            <el-input
              v-model="refreshContent"
              type="textarea"
              :rows="7"
              placeholder="请填写需要刷新的 URL 或目录，支持多行输入&#10;https://www.example.com/image.jpg&#10;https://www.example.com/assets/"
            />
          </el-form-item>
          <p class="status-help">每行一个 URL 或目录。目录以 / 结尾时，会刷新该目录及其子资源。</p>
          <el-button type="primary" :loading="submittingRefresh" :disabled="submittingRefresh" @click="submitRefresh">{{ submittingRefresh ? '提交中…' : '提交刷新' }}</el-button>
          <el-alert
            v-if="latestRefreshTask"
            class="task-feedback"
            type="success"
            show-icon
            :title="`任务 ID ${latestRefreshTask.id || '-'}：${operationStatusText(latestRefreshTask.status || 'QUEUED')}，可在最近操作记录中查看进度。`"
          />
        </el-form>
      </el-card>

      <el-card>
        <template #header>格式说明</template>
        <ul class="check-list">
          <li>仅支持 http/https 协议的 URL。</li>
          <li>每行一个 URL 或目录。</li>
          <li>单次提交后系统将自动去重。</li>
          <li>如需提升配额，请联系你的客户经理。</li>
        </ul>
      </el-card>
    </div>

    <el-card>
      <template #header>最近操作记录</template>
      <el-table :data="refreshRecords">
        <el-table-column label="任务状态">
          <template #default="{ row }">{{ operationStatusText(row.status) }}</template>
        </el-table-column>
        <el-table-column label="提交时间">
          <template #default="{ row }">{{ operationTimeText(row) }}</template>
        </el-table-column>
        <el-table-column label="任务类型">
          <template #default="{ row }">{{ operationTypeText(row.operationType) }}</template>
        </el-table-column>
        <el-table-column label="域名">
          <template #default="{ row }">{{ domainName(row.domainId) }}</template>
        </el-table-column>
        <el-table-column label="数量">
          <template #default="{ row }">{{ row.targets?.length ?? 0 }}</template>
        </el-table-column>
        <el-table-column label="执行结果">
          <template #default="{ row }">{{ operationResult(row) }}</template>
        </el-table-column>
      </el-table>
    </el-card>

    <EmptyState v-if="!profile" title="暂无缓存规则" description="选择域名后从后端读取真实缓存规则。" home-to="/domains" />
    <el-card v-else class="form-card">
      <template #header>缓存规则配置</template>
      <el-form label-position="top" :disabled="savingProfile">
        <el-form-item label="缓存开关"><el-switch v-model="profile.cacheEnabled" /></el-form-item>
        <el-form-item label="默认 TTL 秒"><el-input-number v-model="profile.defaultTtlSeconds" :min="0" /></el-form-item>
        <el-form-item label="浏览器 TTL 秒"><el-input-number v-model="profile.browserTtlSeconds" :min="0" /></el-form-item>
        <el-form-item label="遵循源站 Cache-Control"><el-switch v-model="profile.respectOriginCacheControl" /></el-form-item>
        <el-form-item label="Query 缓存"><el-switch v-model="profile.queryCacheEnabled" /></el-form-item>
        <el-form-item label="忽略 Query 顺序"><el-switch v-model="profile.ignoreQueryOrder" /></el-form-item>
        <el-form-item label="Stale If Error"><el-switch v-model="profile.staleIfErrorEnabled" /></el-form-item>
        <el-button type="primary" :loading="savingProfile" @click="saveProfile">保存缓存规则</el-button>
      </el-form>
    </el-card>
  </section>
</template>
